import { cn } from '../utils';
import { Slot } from '@rn-primitives/slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Platform, Text as RNText, type Role, type TextStyle } from 'react-native';

const textVariants = cva(
  cn(
    'font-sans text-base text-foreground',
    Platform.select({
      web: 'select-text',
    })
  ),
  {
    variants: {
      variant: {
        default: '',
        h1: cn(
          'font-display text-3xl font-bold uppercase leading-none tracking-normal sm:text-5xl',
          Platform.select({ web: 'scroll-m-20 text-balance' })
        ),
        h2: cn(
          'font-display text-3xl font-bold uppercase leading-tight tracking-normal',
          Platform.select({ web: 'scroll-m-20 first:mt-0' })
        ),
        h3: cn(
          'font-display text-2xl font-bold leading-tight tracking-normal',
          Platform.select({ web: 'scroll-m-20' })
        ),
        h4: cn('text-xl font-bold leading-tight', Platform.select({ web: 'scroll-m-20' })),
        p: 'leading-7',
        blockquote: 'mt-4 border-l-2 pl-3 italic sm:mt-6 sm:pl-6',
        code: cn(
          'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold'
        ),
        lead: 'text-xl leading-7 text-muted-foreground',
        large: 'text-lg font-bold',
        small: 'text-sm font-medium leading-none',
        muted: 'text-sm text-muted-foreground',
        mono: 'font-mono text-sm leading-5',
        label: 'font-sans text-xs font-bold uppercase leading-none',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

type TextVariantProps = VariantProps<typeof textVariants>;

type TextVariant = NonNullable<TextVariantProps['variant']>;

const ROLE: Partial<Record<TextVariant, Role>> = {
  h1: 'heading',
  h2: 'heading',
  h3: 'heading',
  h4: 'heading',
  blockquote: Platform.select({ web: 'blockquote' as Role }),
  code: Platform.select({ web: 'code' as Role }),
};

const ARIA_LEVEL: Partial<Record<TextVariant, string>> = {
  h1: '1',
  h2: '2',
  h3: '3',
  h4: '4',
};

type TextClassContextValue =
  | string
  | {
      className?: string;
      style?: TextStyle;
    };

const TextClassContext = React.createContext<TextClassContextValue | undefined>(undefined);

function Text({
  className,
  asChild = false,
  variant = 'default',
  style,
  ...props
}: React.ComponentProps<typeof RNText> &
  React.RefAttributes<typeof RNText> &
  TextVariantProps & {
    asChild?: boolean;
  }) {
  const textContext = React.useContext(TextClassContext);
  const textClass = typeof textContext === 'string' ? textContext : textContext?.className;
  const textStyle = typeof textContext === 'string' ? undefined : textContext?.style;
  const Component = asChild ? Slot : RNText;
  return (
    <Component
      className={cn(textVariants({ variant }), textClass, className)}
      role={variant ? ROLE[variant] : undefined}
      aria-level={variant ? ARIA_LEVEL[variant] : undefined}
      style={[textStyle, style]}
      {...props}
    />
  );
}

export { Text, TextClassContext };
